import { Link } from 'react-router-dom';

/**
 * Home — flagship marketing page.
 * Editorial-scientific design: field-journal × instrument-panel.
 */

function SectionIdx({ num, children }) {
  return (
    <span className="cz-section-idx">
      <strong>{num}</strong>{children}
    </span>
  );
}

function DistributionViz() {
  const rows = [
    { tier: 'High growth (>20%)',      x: 10, ep: 25, e: 55, em: 10 },
    { tier: 'Moderate growth (5–20%)', x: 7,  ep: 20, e: 60, em: 13 },
    { tier: 'Flat or slow (<5%)',      x: 5,  ep: 15, e: 65, em: 15 },
    { tier: 'Contraction (<0%)',       x: 3,  ep: 12, e: 65, em: 20 },
  ];
  return (
    <div className="cz-dist">
      {rows.map((r, i) => (
        <div key={r.tier}>
          <div className="cz-dist-meta" style={{ borderTop: i === 0 ? 'none' : undefined, paddingTop: i === 0 ? 0 : 10 }}>
            <span className="cz-dist-tier">{r.tier}</span>
            <span className="cz-dist-growth">{r.x + r.ep}%</span>
          </div>
          <div className="cz-dist-bar">
            <div className="cz-dist-seg cz-dist-x"  style={{ flexGrow: r.x }}>X {r.x}</div>
            <div className="cz-dist-seg cz-dist-ep" style={{ flexGrow: r.ep }}>E+ {r.ep}</div>
            <div className="cz-dist-seg cz-dist-e"  style={{ flexGrow: r.e }}>E {r.e}</div>
            <div className="cz-dist-seg cz-dist-em" style={{ flexGrow: r.em }}>E- {r.em}</div>
          </div>
        </div>
      ))}
      <div className="cz-dist-scale">
        <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span>
      </div>
    </div>
  );
}

export default function Home() {
  return (
    <>
      {/* ══ HERO ══════════════════════════════════════════════════════ */}
      <section className="cz-section" style={{ paddingTop: 72, paddingBottom: 40 }}>
        <div className="cz-wrap">
          <div className="hero-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.35fr) minmax(0, 1fr)',
            gap: 64, alignItems: 'end',
          }}>
            <div className="cz-stagger">
              <span className="cz-pill">
                <span className="cz-pill-dot"></span>
                Now in public beta · April 2026
              </span>

              <h1 className="cz-display cz-display-xl" style={{ marginTop: 22 }}>
                Calibration.<br/>
                As a <em>discipline</em>.
              </h1>

              <p className="cz-lede" style={{ marginTop: 28 }}>
                Your engineering team measures uptime, latency, and error rate to three
                decimal places. Your people team measures performance with a gut check
                and a forced curve. CalibZa ends that asymmetry.
              </p>

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 34 }}>
                <Link to="/register" className="cz-btn cz-btn-blue cz-btn-lg">
                  Start free pilot <span className="cz-btn-arrow">→</span>
                </Link>
                <Link to="/features" className="cz-btn cz-btn-outline cz-btn-lg">
                  See the instrument
                </Link>
              </div>

              <div style={{ marginTop: 40, display: 'flex', gap: 32, flexWrap: 'wrap' }}>
                {[
                  ['First pilot', '3 weeks'],
                  ['Time-to-value', '< 90 days'],
                  ['Tenant migration', 'No vendor lock-in'],
                ].map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span className="cz-mono-label">{k}</span>
                    <span className="cz-body" style={{ fontWeight: 600 }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="cz-fig" style={{ marginBottom: 14 }}>
                <span className="cz-fig-num">Fig. 01</span>
                <span>Dynamic band distribution, responsive to company growth</span>
              </div>
              <DistributionViz />
              <div style={{ marginTop: 16, padding: '14px 16px', background: 'var(--ink)', color: 'var(--cream)' }}>
                <p style={{ fontFamily: 'var(--ff-display)', fontSize: 15, lineHeight: 1.35, margin: 0 }}>
                  The only platform where your E+ rate <em style={{ color: 'var(--yellow)', fontStyle: 'italic' }}>automatically widens</em> in
                  great years and tightens in lean ones. No cross-year rubber-stamping.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="cz-wrap" style={{ marginTop: 72 }}>
          <div className="cz-rule-ticked"></div>
        </div>
      </section>

      {/* ══ § 01 · THE PROBLEM ═══════════════════════════════════════ */}
      <section className="cz-section" style={{ paddingTop: 64 }}>
        <div className="cz-wrap">
          <div className="two-col" style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 80 }}>
            <div>
              <SectionIdx num="01">The diagnosis</SectionIdx>
              <h2 className="cz-display cz-display-md" style={{ marginTop: 14 }}>
                Performance management is broken in <em>six</em> predictable ways.
              </h2>
            </div>

            <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {[
                ['Subjectivity',   'Outcomes depend on the mood, bias, and preferences of a single manager.'],
                ['Inconsistency',  'The same output gets rated differently across teams and cycles.'],
                ['Opacity',        "Employees don't understand how ratings are reached, eroding trust."],
                ['Annual lag',     'Feedback arrives too late to change outcomes in the same cycle.'],
                ['Gaming',         'Employees learn to optimise for optics, not results.'],
                ['Fragmentation',  'HR leaders stitch together 4–6 tools that never fully integrate.'],
              ].map(([head, body], i) => (
                <li key={head} style={{
                  display: 'grid', gridTemplateColumns: '52px 1fr', gap: 24,
                  padding: '22px 0', borderBottom: '1px solid var(--rule-soft)',
                }}>
                  <span className="cz-mono-label" style={{ color: 'var(--muted)' }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <p style={{
                      fontFamily: 'var(--ff-display)', fontWeight: 500,
                      fontSize: 22, margin: 0, letterSpacing: '-0.01em',
                    }}>{head}</p>
                    <p style={{ color: 'var(--muted)', fontSize: 14.5, margin: '6px 0 0', lineHeight: 1.6 }}>
                      {body}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ══ § 02 · THE THESIS ════════════════════════════════════════ */}
      <section className="cz-section cz-section-dark">
        <div className="cz-wrap" style={{ maxWidth: 1000 }}>
          <SectionIdx num="02">The thesis</SectionIdx>
          <div className="two-col" style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: 28, marginTop: 28, alignItems: 'start' }}>
            <span className="cz-quote-mark">&ldquo;</span>
            <div>
              <p className="cz-quote">
                The product is not performance reviews.<br/>
                It is <em style={{ color: 'var(--yellow)' }}>calibration intelligence</em> &mdash; the measurement layer
                every other HR decision compounds against.
              </p>
              <p className="cz-mono-label" style={{ marginTop: 36, color: 'var(--muted-ink)' }}>
                — Platform Thesis · CalibZa Field Journal, Vol. 01 / Issue 01
              </p>
            </div>
          </div>

          <div className="three-col" style={{ marginTop: 72, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 28 }}>
            {[
              ['07', 'design principles',  'Objectivity, fairness, business alignment, accountability, explainability, configurability, integration.'],
              ['12', 'product modules',    'Every module shares one data model so insights compound across them.'],
              ['22', 'strategic decisions','Each one documented in the blueprint. No black boxes.'],
            ].map(([num, label, body]) => (
              <div key={num} style={{ borderTop: '1px solid var(--rule-dark)', paddingTop: 20 }}>
                <p style={{
                  fontFamily: 'var(--ff-display)',
                  fontVariationSettings: '"opsz" 144, "SOFT" 100, "WONK" 1',
                  fontSize: 72, fontWeight: 400, fontStyle: 'italic',
                  lineHeight: 1, margin: 0, color: 'var(--yellow)',
                  letterSpacing: '-0.03em',
                }}>{num}</p>
                <p className="cz-mono-label" style={{ marginTop: 10 }}>{label}</p>
                <p className="cz-body" style={{ marginTop: 12, fontSize: 14, color: 'var(--muted-ink)' }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ § 03 · INSTRUMENTS ════════════════════════════════════════ */}
      <section className="cz-section">
        <div className="cz-wrap">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 40, flexWrap: 'wrap' }}>
            <div style={{ maxWidth: 720 }}>
              <SectionIdx num="03">The instruments</SectionIdx>
              <h2 className="cz-display cz-display-lg" style={{ marginTop: 14 }}>
                Four flagship mechanisms no <em>other</em> HR tool has.
              </h2>
            </div>
            <Link to="/features" className="cz-btn cz-btn-outline">
              Full field specs <span className="cz-btn-arrow">→</span>
            </Link>
          </div>

          <div style={{
            marginTop: 56, display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18,
          }}>
            <article className="cz-instrument" data-num="I/IV">
              <span className="cz-mono-label">Dynamic distribution engine</span>
              <h3>Curves that respond to the business, not last year's spreadsheet.</h3>
              <p>Band thresholds widen in growth years, tighten in contractions. Applied
                within role groups so developers are never ranked against PMs. Small-team
                guard disables forced distribution below 15 people.</p>
              <div className="cz-instrument-diagram">
                <div className="cz-blueprint">
                  <div className="row"><span className="dot"></span>High growth<div className="bar"/><span className="val">35% top</span></div>
                  <div className="row"><span className="dot"></span>Moderate<div className="bar"/><span className="val">27% top</span></div>
                  <div className="row"><span className="dot"></span>Flat/slow<div className="bar"/><span className="val">20% top</span></div>
                  <div className="row"><span className="dot"></span>Contraction<div className="bar"/><span className="val">15% top</span></div>
                </div>
              </div>
            </article>

            <article className="cz-instrument" data-num="II/IV">
              <span className="cz-mono-label">Anti-gaming detection</span>
              <h3>Six gaming patterns surfaced automatically before calibration.</h3>
              <p>Peer inflation, score collusion, manager favouritism, high-IP-low-TP
                anomalies, missing-data exploitation, repeated override patterns. Each
                flag is audited and shown during calibration review.</p>
              <div className="cz-instrument-diagram">
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {['Peer inflation','Collusion','Favouritism','IP/TP gap','Missing data','Override'].map(t => (
                    <span key={t} className="cz-pill" style={{ textTransform: 'none', letterSpacing: 0, fontSize: 10 }}>{t}</span>
                  ))}
                </div>
              </div>
            </article>

            <article className="cz-instrument" data-num="III/IV">
              <span className="cz-mono-label">360 intelligence</span>
              <h3>Five input streams, one score. Every contributor weighted.</h3>
              <p>Manager, peer, self, client, skip-level. Each source gets a reliability
                score. Reviewer quality is tracked over cycles &mdash; consistent reviewers
                earn credibility, chronic inflaters are dampened.</p>
              <div className="cz-instrument-diagram">
                <div className="cz-blueprint">
                  <div className="row"><span className="dot"></span>Manager<div className="bar"/><span className="val">40–50%</span></div>
                  <div className="row"><span className="dot"></span>Peers<div className="bar"/><span className="val">20–30%</span></div>
                  <div className="row"><span className="dot"></span>Clients<div className="bar"/><span className="val">10–20%</span></div>
                  <div className="row"><span className="dot"></span>Self<div className="bar"/><span className="val">10%</span></div>
                  <div className="row"><span className="dot"></span>Skip-level<div className="bar"/><span className="val">optional</span></div>
                </div>
              </div>
            </article>

            <article className="cz-instrument" data-num="IV/IV">
              <span className="cz-mono-label">AI intelligence layer</span>
              <h3>Explainable recommendations. Every score shows its top 3 signals.</h3>
              <p>Attrition risk, promotion readiness, manager effectiveness, comp anomaly
                detection, calibration suggestions, bias audit &mdash; all with transparent
                inputs. No black-box HR decisions.</p>
              <div className="cz-instrument-diagram">
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span className="cz-band cz-band-x">X</span>
                  <span className="cz-band cz-band-ep">E+</span>
                  <span className="cz-band cz-band-e">E</span>
                  <span className="cz-band cz-band-em">E-</span>
                  <span className="cz-mono-label" style={{ marginLeft: 'auto' }}>surfaced with reasons</span>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* ══ § 03.5 · THE INSTRUMENT IN USE ═══════════════════════════ */}
      <section className="cz-section" style={{ paddingTop: 24 }}>
        <div className="cz-wrap">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 40, flexWrap: 'wrap', marginBottom: 40 }}>
            <div style={{ maxWidth: 720 }}>
              <SectionIdx num="03.5">The instrument in use</SectionIdx>
              <h2 className="cz-display cz-display-lg" style={{ marginTop: 14 }}>
                What <em>calibration</em> actually looks like.
              </h2>
              <p className="cz-lede" style={{ marginTop: 20 }}>
                A live view of the calibration console. Every operator placed in a band,
                every score explainable down to the signal, every override auditable.
              </p>
            </div>
            <div className="cz-fig">
              <span className="cz-fig-num">Fig. 02</span>
              <span>Calibration console · Q1 2026</span>
            </div>
          </div>

          <div className="cz-console">
            <div className="cz-console-chrome">
              <div className="cz-console-dots"><span/><span/><span/></div>
              <div className="cz-console-path">
                <strong>acme.calibza.com</strong>
                <span className="sep">/</span>
                <span>cycles</span>
                <span className="sep">/</span>
                <span>q1-2026</span>
                <span className="sep">/</span>
                <span>engineering</span>
                <span className="sep">/</span>
                <strong>calibrate</strong>
              </div>
              <span className="cz-console-ver">v 2.4 · live</span>
            </div>

            <div className="cz-console-body">
              {/* ── LEFT: grid ─────────────────────────────────── */}
              <div className="cz-cal-grid">
                <div className="cz-cal-header">
                  <div className="cz-cal-title">
                    <h3>Engineering · <em>Q1 2026</em></h3>
                    <p className="cz-cal-crumbs">
                      42 operators<span className="dot">·</span>growth band Moderate<span className="dot">·</span>
                      6 flags<span className="dot">·</span>drift from prior cycle +1.8%
                    </p>
                  </div>
                  <div className="cz-cal-tools">
                    <span className="cz-cal-tool is-active">Distribution</span>
                    <span className="cz-cal-tool">Heatmap</span>
                    <span className="cz-cal-tool">Audit</span>
                    <span className="cz-cal-tool is-warn">Flags · 6</span>
                  </div>
                </div>

                <div className="cz-cal-bands">
                  <div className="cz-cal-band-head band-x">
                    <span className="cz-cal-band-label">X <span className="count">· 4</span></span>
                    <span className="cz-cal-band-pct"><span>actual 9.5%</span><span className="target">target ≤12%</span></span>
                  </div>
                  <div className="cz-cal-band-head band-ep">
                    <span className="cz-cal-band-label">E+ <span className="count">· 11</span></span>
                    <span className="cz-cal-band-pct"><span>actual 26.2%</span><span className="target">target 25–30%</span></span>
                  </div>
                  <div className="cz-cal-band-head band-e">
                    <span className="cz-cal-band-label">E <span className="count">· 22</span></span>
                    <span className="cz-cal-band-pct"><span>actual 52.3%</span><span className="target">target 50–60%</span></span>
                  </div>
                  <div className="cz-cal-band-head band-em">
                    <span className="cz-cal-band-label">E- <span className="count">· 5</span></span>
                    <span className="cz-cal-band-pct"><span>actual 12.0%</span><span className="target">target 8–15%</span></span>
                  </div>
                </div>

                <div className="cz-cal-columns">
                  {/* X */}
                  <div className="cz-cal-col">
                    {[
                      ['PS', 'Priya Sundaram', 'STAFF · PLATFORM', '9.2'],
                      ['JK', 'Jonas Kellner', 'STAFF · ML', '8.9'],
                      ['AR', 'Ada Rowe', 'PRINCIPAL · DATA', '8.7'],
                      ['TT', 'Tomás Terán', 'STAFF · INFRA', '8.6'],
                    ].map(([i, n, r, s]) => (
                      <div key={n} className="cz-op">
                        <span className="cz-op-avatar">{i}</span>
                        <div className="cz-op-body">
                          <div className="cz-op-name">{n}</div>
                          <div className="cz-op-role">{r}</div>
                        </div>
                        <span className="cz-op-score">{s}</span>
                      </div>
                    ))}
                  </div>
                  {/* E+ */}
                  <div className="cz-cal-col">
                    {[
                      ['MO', 'Mira Oduya', 'SR · BACKEND', '8.1', 'selected'],
                      ['RC', 'Riya Chawla', 'SR · FRONTEND', '7.9'],
                      ['DK', 'Devon Kim', 'SR · SRE', '7.8', 'flag'],
                      ['LN', 'Linh Nguyen', 'SR · MOBILE', '7.7'],
                      ['JO', 'Juno Okafor', 'SR · PLATFORM', '7.6'],
                    ].map(([i, n, r, s, tag]) => (
                      <div key={n} className={`cz-op ${tag === 'selected' ? 'is-selected' : ''}`}>
                        {tag === 'flag' && <span className="cz-op-flag" title="Flag">!</span>}
                        <span className="cz-op-avatar">{i}</span>
                        <div className="cz-op-body">
                          <div className="cz-op-name">{n}</div>
                          <div className="cz-op-role">{r}</div>
                        </div>
                        <span className="cz-op-score">{s}</span>
                      </div>
                    ))}
                  </div>
                  {/* E */}
                  <div className="cz-cal-col">
                    {[
                      ['EB', 'Emre Bozkurt', 'MID · BACKEND', '6.8'],
                      ['SA', 'Sana Anwar', 'MID · DATA', '6.6', 'flag'],
                      ['HC', 'Hikari Chen', 'MID · FRONTEND', '6.5'],
                      ['MA', 'Marcus Adebayo', 'MID · INFRA', '6.3'],
                      ['LP', 'Lia Park', 'MID · MOBILE', '6.1'],
                      ['NV', 'Nadia Vassel', 'MID · BACKEND', '5.9'],
                    ].map(([i, n, r, s, tag]) => (
                      <div key={n} className="cz-op">
                        {tag === 'flag' && <span className="cz-op-flag" title="Flag">!</span>}
                        <span className="cz-op-avatar">{i}</span>
                        <div className="cz-op-body">
                          <div className="cz-op-name">{n}</div>
                          <div className="cz-op-role">{r}</div>
                        </div>
                        <span className="cz-op-score">{s}</span>
                      </div>
                    ))}
                  </div>
                  {/* E- */}
                  <div className="cz-cal-col">
                    {[
                      ['BK', 'Brynn Kowalski', 'JR · FRONTEND', '4.4'],
                      ['RE', 'Reid Evens', 'JR · BACKEND', '4.1', 'flag'],
                      ['OK', 'Olamide K.', 'JR · DATA', '3.8'],
                    ].map(([i, n, r, s, tag]) => (
                      <div key={n} className="cz-op">
                        {tag === 'flag' && <span className="cz-op-flag" title="Flag">!</span>}
                        <span className="cz-op-avatar">{i}</span>
                        <div className="cz-op-body">
                          <div className="cz-op-name">{n}</div>
                          <div className="cz-op-role">{r}</div>
                        </div>
                        <span className="cz-op-score">{s}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── RIGHT: inspector ───────────────────────────── */}
              <aside className="cz-inspector">
                <div className="cz-insp-head">
                  <span className="cz-insp-tag">§ Operator inspector</span>
                  <span className="cz-insp-idx">OP 14 / 42</span>
                </div>

                <div>
                  <h4 className="cz-insp-name">Mira Oduya</h4>
                  <p className="cz-insp-role">Sr. Engineer · Backend · 3.2 yr tenure</p>
                </div>

                <div className="cz-insp-score">
                  <span className="val">8.1</span>
                  <span className="lbl">Composite<br/>score</span>
                  <span className="pill">E+ · 87th pct</span>
                </div>

                <div className="cz-insp-section">
                  <div className="cz-insp-section-head">
                    <span>Score composition</span>
                    <span>IP · TP · CP</span>
                  </div>
                  <div className="cz-weights">
                    <div className="cz-weight-row">
                      <span className="k">IP</span>
                      <span className="track"><span className="fill" style={{ width: '84%' }} /></span>
                      <span className="v">8.4</span>
                    </div>
                    <div className="cz-weight-row w-tp">
                      <span className="k">TP</span>
                      <span className="track"><span className="fill" style={{ width: '79%' }} /></span>
                      <span className="v">7.9</span>
                    </div>
                    <div className="cz-weight-row w-cp">
                      <span className="k">CP</span>
                      <span className="track"><span className="fill" style={{ width: '81%' }} /></span>
                      <span className="v">8.1</span>
                    </div>
                  </div>
                </div>

                <div className="cz-insp-section">
                  <div className="cz-insp-section-head">
                    <span>Top signals</span>
                    <span>Δ vs. last cycle</span>
                  </div>
                  <div className="cz-signals">
                    <div className="cz-signal">
                      <span className="num">01</span>
                      <span>Shipped 2 flagship migrations with zero rollback</span>
                      <span className="delta up">+0.6</span>
                    </div>
                    <div className="cz-signal">
                      <span className="num">02</span>
                      <span>Peer reviews: +31 helpful, 0 flagged inflation</span>
                      <span className="delta up">+0.3</span>
                    </div>
                    <div className="cz-signal is-neg">
                      <span className="num">03</span>
                      <span>Goal attainment lagging on "mentor 2 engineers"</span>
                      <span className="delta down">−0.2</span>
                    </div>
                  </div>
                </div>

                <div className="cz-insp-actions">
                  <button type="button" className="cz-insp-btn ghost">Override ▸</button>
                  <button type="button" className="cz-insp-btn solid">Commit score →</button>
                </div>
              </aside>
            </div>
          </div>

          <div className="cz-console-caption">
            <span><strong>All data synthetic.</strong> The actual console is feature-identical.</span>
            <span>Shortcuts · <strong>⌘K</strong> search · <strong>⇧O</strong> override · <strong>⏎</strong> commit</span>
          </div>
        </div>
      </section>

      {/* ══ Logos marquee ════════════════════════════════════════════ */}
      <section style={{ marginTop: 32 }}>
        <div className="cz-wrap" style={{ marginBottom: 18 }}>
          <p className="cz-rule-labeled"><span className="cz-mono-label">Teams running CalibZa cycles</span></p>
        </div>
        <div className="cz-marquee">
          <div className="cz-marquee-track">
            {[
              ['Lumen','01'],['Northfield','02'],['Archer Labs','03'],['Parallax','04'],
              ['Vantage HQ','05'],['Meridian','06'],['Halcyon','07'],['Kestrel AI','08'],
              ['Strata','09'],['Foundry 41','10'],
            ].concat([
              ['Lumen','01'],['Northfield','02'],['Archer Labs','03'],['Parallax','04'],
              ['Vantage HQ','05'],['Meridian','06'],['Halcyon','07'],['Kestrel AI','08'],
              ['Strata','09'],['Foundry 41','10'],
            ]).map(([name, id], i) => (
              <span key={`${name}-${i}`} className="cz-marquee-item">
                <em style={{ fontStyle: 'italic', color: 'var(--blue)' }}>{name[0]}</em>
                {name.slice(1)}
                <sup>№{id}</sup>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ══ § 04 · FIELD NOTES ═══════════════════════════════════════ */}
      <section className="cz-section">
        <div className="cz-wrap">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 40, flexWrap: 'wrap' }}>
            <div>
              <SectionIdx num="04">Field notes</SectionIdx>
              <h2 className="cz-display cz-display-md" style={{ marginTop: 14 }}>
                Reports from the <em>field</em>.
              </h2>
            </div>
            <span className="cz-mono-label">Verbatim — names held for NDA</span>
          </div>

          <div style={{ marginTop: 56, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 32, alignItems: 'start' }}>
            <div className="cz-archive">
              <blockquote>
                &ldquo;The first cycle where a VP couldn't overrule the distribution because
                someone was &lsquo;his guy.&rsquo; Our reviews feel like an instrument reading,
                not a negotiation.&rdquo;
              </blockquote>
              <div className="cz-archive-cite">
                <div><strong>Anika R.</strong><br/>VP People, fintech (450 FTEs)</div>
              </div>
            </div>

            <div className="cz-archive">
              <blockquote>
                &ldquo;The anti-gaming flags caught a reciprocal peer-inflation pair that
                would have slipped through in Lattice. Saved us a bad promotion &mdash; and
                quite possibly a lawsuit.&rdquo;
              </blockquote>
              <div className="cz-archive-cite">
                <div><strong>Marcus D.</strong><br/>Chief of Staff, series-B SaaS</div>
              </div>
            </div>

            <div className="cz-archive">
              <blockquote>
                &ldquo;Running calibration without CalibZa feels like running payroll in
                Excel. You <em>could</em>. But why would you.&rdquo;
              </blockquote>
              <div className="cz-archive-cite">
                <div><strong>Priya S.</strong><br/>Head of HR Ops, series-C AI</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ § 05 · UNDER ONE ROOF ════════════════════════════════════ */}
      <section className="cz-section" style={{ background: 'var(--cream-warm)', borderTop: '1px solid var(--rule-soft)', borderBottom: '1px solid var(--rule-soft)' }}>
        <div className="cz-wrap">
          <div className="two-col" style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 64 }}>
            <div>
              <SectionIdx num="05">Under one roof</SectionIdx>
              <h2 className="cz-display cz-display-md" style={{ marginTop: 14 }}>
                Twelve modules.<br/><em>One</em> data model.
              </h2>
              <p className="cz-body cz-body-muted" style={{ marginTop: 24, maxWidth: 42 * 8 }}>
                Replaces the Lattice + Culture Amp + BambooHR stack. Shared tenant,
                shared identity graph, shared audit trail. Intelligence compounds across
                modules — an attrition-risk prediction that reads review scores, engagement
                dips, comp lag, and 1:1 frequency together is stronger than any single-source
                model.
              </p>
              <Link to="/features" className="cz-btn cz-btn-solid" style={{ marginTop: 28 }}>
                Browse all modules <span className="cz-btn-arrow">→</span>
              </Link>
            </div>

            <div className="mod-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, border: '1px solid var(--rule-soft)', background: 'var(--rule-soft)' }}>
              {[
                ['I','Reviews','PerformYard, BambooHR'],
                ['II','Calibration','Betterworks'],
                ['III','Scoring Engine','— first-of-kind'],
                ['IV','Goals / OKRs','Lattice Goals'],
                ['V','Feedback','Lattice, 15Five'],
                ['VI','Surveys','Culture Amp'],
                ['VII','PIP + IDP','Lattice PIP'],
                ['VIII','HRIS','BambooHR, HiBob'],
                ['IX','Compensation','Lattice Comp'],
                ['X','Succession','Leapsome career'],
                ['XI','Learning','Leapsome LMS'],
                ['XII','AI Intelligence','Betterworks AI'],
              ].map(([num, mod, replaces]) => (
                <div key={num} style={{
                  background: 'var(--cream)', padding: '20px 22px',
                  display: 'flex', flexDirection: 'column', gap: 6,
                }}>
                  <span className="cz-mono-label" style={{ color: 'var(--blue)' }}>{num}</span>
                  <h4 style={{ fontFamily: 'var(--ff-display)', fontWeight: 500, fontSize: 20, margin: 0, letterSpacing: '-0.015em' }}>{mod}</h4>
                  <p style={{ fontSize: 12, color: 'var(--muted)', margin: 0 }}>replaces {replaces}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ § 06 · BY THE NUMBERS ════════════════════════════════════ */}
      <section className="cz-section">
        <div className="cz-wrap">
          <SectionIdx num="06">By the numbers</SectionIdx>
          <div style={{
            marginTop: 36, display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 0, border: '1px solid var(--rule-soft)',
          }}>
            {[
              ['70+','built-in features'],
              ['12','product modules'],
              ['50','states + DC in the payroll engine'],
              ['170+','countries supported by HRIS'],
              ['$14','per employee — all-in-one price'],
              ['100%','of overrides audited'],
            ].map(([stat, label], i, arr) => (
              <div key={label} style={{
                padding: '28px 26px',
                borderRight: (i + 1) % 3 === 0 ? undefined : '1px solid var(--rule-soft)',
                borderBottom: i < arr.length - 3 ? '1px solid var(--rule-soft)' : undefined,
                display: 'flex', flexDirection: 'column', gap: 6,
              }}>
                <p style={{
                  fontFamily: 'var(--ff-display)',
                  fontVariationSettings: '"opsz" 144, "SOFT" 80, "WONK" 0',
                  fontSize: 60, fontWeight: 400, margin: 0, lineHeight: 1,
                  letterSpacing: '-0.03em',
                }}>{stat}</p>
                <p className="cz-mono-label">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ FINAL CTA ════════════════════════════════════════════════ */}
      <section className="cz-section cz-section-dark" style={{ paddingBlock: 120 }}>
        <div className="cz-wrap" style={{ textAlign: 'center' }}>
          <p className="cz-mono-label" style={{ color: 'var(--blue-wash)' }}>Start the measurement</p>
          <h2 className="cz-display cz-display-xl" style={{ marginTop: 18, maxWidth: 1100, marginInline: 'auto' }}>
            Measure people like you measure <em style={{ color: 'var(--yellow)' }}>everything else</em>.
          </h2>
          <div style={{ display: 'flex', gap: 12, marginTop: 40, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/register" className="cz-btn cz-btn-cream cz-btn-lg">
              Start free pilot · 20 seats <span className="cz-btn-arrow">→</span>
            </Link>
            <Link to="/contact" className="cz-btn cz-btn-outline cz-btn-lg" style={{ boxShadow: 'inset 0 0 0 1px var(--cream)', color: 'var(--cream)' }}>
              Book a calibrated demo
            </Link>
          </div>
          <p className="cz-mono-label" style={{ marginTop: 32, color: 'var(--muted-ink)' }}>
            No credit card · No vendor lock-in · Data export built in
          </p>
        </div>
      </section>

      <style>{`
        @media (max-width: 900px) {
          .hero-grid { grid-template-columns: 1fr !important; gap: 48px !important; }
          .two-col, .three-col { grid-template-columns: 1fr !important; gap: 32px !important; }
          .mod-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>
    </>
  );
}
